import React, { useCallback, useEffect, useRef, useState } from 'react';
import './MiniGamesPage.css';

const BEST_SCORE_KEY = 'starDeliCornedBeefRunBest';

const POWERUP_DETAILS = {
  shield: { label: 'Deli Shield', duration: 9 },
  double: { label: 'Double Deck', duration: 10 },
  magnet: { label: 'Counter Magnet', duration: 8 }
};

const POWERUP_TYPES = Object.keys(POWERUP_DETAILS);
const LANES = [0, 1, 2];

const getLaneCenters = (game) => {
  const top = Math.max(112, game.height * 0.36);
  const bottom = game.groundY - 28;
  const middle = (top + bottom) / 2;
  return [top, middle, bottom];
};

const getLaneY = (game, lane, height) => getLaneCenters(game)[lane] - height / 2;

const makeGameState = (width = 900, height = 360) => {
  const groundY = height - 62;
  const game = {
    width,
    height,
    groundY,
    age: 0,
    distance: 0,
    speed: 178,
    spawnTimer: 1.35,
    powerupTimer: 4.25,
    lastTime: 0,
    runScore: 0,
    lives: 3,
    shake: 0,
    hitFlash: 0,
    invincible: 0,
    comboText: { text: '', time: 0 },
    effects: {
      shield: 0,
      double: 0,
      magnet: 0
    },
    player: {
      x: 94,
      lane: 1,
      width: 86,
      height: 52,
      y: 0
    },
    obstacles: [],
    items: []
  };

  game.player.y = getLaneY(game, game.player.lane, game.player.height);
  return game;
};

const roundedRect = (ctx, x, y, width, height, radius) => {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const shuffle = (values) => [...values].sort(() => Math.random() - 0.5);

const drawShelfItem = (ctx, x, y, index) => {
  const variant = index % 4;

  if (variant === 0) {
    const jar = ctx.createLinearGradient(x, y, x, y + 44);
    jar.addColorStop(0, 'rgba(255, 248, 219, 0.88)');
    jar.addColorStop(1, 'rgba(144, 155, 84, 0.78)');
    ctx.fillStyle = jar;
    roundedRect(ctx, x, y + 8, 26, 36, 7);
    ctx.fill();
    ctx.fillStyle = '#5c2f1d';
    ctx.fillRect(x + 3, y + 4, 20, 7);
    ctx.fillStyle = 'rgba(255,255,255,0.34)';
    ctx.fillRect(x + 5, y + 13, 5, 24);
    return;
  }

  if (variant === 1) {
    ctx.fillStyle = '#d4bb78';
    roundedRect(ctx, x, y + 17, 40, 12, 6);
    ctx.fill();
    ctx.strokeStyle = '#7a4b23';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(x + 12, y + 19, 12, 0.18, Math.PI * 1.7);
    ctx.arc(x + 27, y + 19, 12, Math.PI * 1.25, Math.PI * 2.92);
    ctx.stroke();
    return;
  }

  if (variant === 2) {
    ctx.fillStyle = '#f2e4bd';
    roundedRect(ctx, x, y + 8, 32, 38, 4);
    ctx.fill();
    ctx.strokeStyle = 'rgba(166, 74, 50, 0.55)';
    ctx.lineWidth = 2;
    for (let i = 0; i < 3; i += 1) {
      ctx.beginPath();
      ctx.moveTo(x + 6, y + 18 + i * 9);
      ctx.lineTo(x + 26, y + 18 + i * 9);
      ctx.stroke();
    }
    return;
  }

  const meat = ctx.createLinearGradient(x, y + 10, x + 46, y + 38);
  meat.addColorStop(0, '#8f2f36');
  meat.addColorStop(0.48, '#d88585');
  meat.addColorStop(1, '#682127');
  ctx.fillStyle = meat;
  roundedRect(ctx, x, y + 18, 46, 20, 10);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255, 229, 211, 0.35)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x + 8, y + 30);
  ctx.quadraticCurveTo(x + 20, y + 22, x + 34, y + 32);
  ctx.stroke();
};

const drawCounter = (ctx, game) => {
  const { width, height, groundY, distance } = game;
  const laneCenters = getLaneCenters(game);

  ctx.clearRect(0, 0, width, height);

  const wallGlow = ctx.createLinearGradient(0, 0, 0, height);
  wallGlow.addColorStop(0, '#5a341e');
  wallGlow.addColorStop(0.38, '#8a6033');
  wallGlow.addColorStop(0.76, '#3d2116');
  wallGlow.addColorStop(1, '#20100a');
  ctx.fillStyle = wallGlow;
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = 'rgba(31, 16, 9, 0.62)';
  ctx.fillRect(0, 18, width, 68);
  ctx.fillStyle = '#b38b39';
  ctx.fillRect(0, 82, width, 5);
  ctx.fillStyle = 'rgba(255,255,255,0.18)';
  ctx.fillRect(0, 83, width, 1);

  for (let i = -1; i < Math.ceil(width / 146) + 2; i += 1) {
    const x = i * 146 - (distance * 0.06) % 146;
    drawShelfItem(ctx, x + 28, 26, i + Math.floor(distance / 620));
  }

  const caseTop = 100;
  const caseGradient = ctx.createLinearGradient(0, caseTop, 0, groundY + 24);
  caseGradient.addColorStop(0, 'rgba(251, 242, 212, 0.28)');
  caseGradient.addColorStop(0.62, 'rgba(232, 201, 142, 0.2)');
  caseGradient.addColorStop(1, 'rgba(88, 46, 25, 0.08)');
  ctx.fillStyle = caseGradient;
  ctx.fillRect(0, caseTop, width, groundY - caseTop + 24);

  ctx.strokeStyle = 'rgba(255,255,255,0.28)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, caseTop);
  ctx.lineTo(width, caseTop);
  ctx.stroke();

  laneCenters.forEach((center, index) => {
    const laneTop = center - 23;
    const laneGradient = ctx.createLinearGradient(0, laneTop, 0, laneTop + 46);
    laneGradient.addColorStop(0, 'rgba(255, 241, 204, 0.18)');
    laneGradient.addColorStop(1, 'rgba(70, 34, 18, 0.1)');
    ctx.fillStyle = laneGradient;
    ctx.fillRect(0, laneTop, width, 46);

    ctx.strokeStyle = index === 1 ? 'rgba(198, 154, 34, 0.58)' : 'rgba(255, 238, 177, 0.3)';
    ctx.lineWidth = index === 1 ? 3 : 2;
    ctx.beginPath();
    ctx.moveTo(0, center + 26);
    ctx.lineTo(width, center + 26);
    ctx.stroke();
  });

  ctx.strokeStyle = 'rgba(74, 38, 28, 0.26)';
  ctx.lineWidth = 1;
  for (let i = -120; i < width + 120; i += 70) {
    const x = i - (distance * 0.28) % 70;
    ctx.beginPath();
    ctx.moveTo(x, groundY + 30);
    ctx.lineTo(x + 48, height);
    ctx.stroke();
  }

  const counterGradient = ctx.createLinearGradient(0, groundY + 18, 0, height);
  counterGradient.addColorStop(0, '#f1dfb2');
  counterGradient.addColorStop(0.26, '#b9854f');
  counterGradient.addColorStop(1, '#4c2818');
  ctx.fillStyle = counterGradient;
  ctx.fillRect(0, groundY + 18, width, height - groundY - 18);

  ctx.fillStyle = 'rgba(255, 255, 255, 0.28)';
  ctx.fillRect(0, groundY + 22, width, 4);
};

const drawShieldAura = (ctx, player, distance) => {
  const pulse = 0.5 + Math.sin(distance * 0.08) * 0.08;
  const cx = player.x + player.width / 2;
  const cy = player.y + player.height / 2;

  ctx.save();
  ctx.strokeStyle = `rgba(244, 212, 106, ${pulse})`;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.ellipse(cx, cy, player.width * 0.7, player.height * 0.84, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(255,255,255,0.34)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.ellipse(cx - 8, cy - 4, player.width * 0.5, player.height * 0.55, -0.18, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
};

const drawCornedBeefRunner = (ctx, player, distance, effects, invincible) => {
  const { x, y, width, height } = player;
  const bob = Math.sin(distance * 0.06) * 1.4;
  const blink = invincible > 0 && Math.floor(invincible * 12) % 2 === 0;

  if (effects.shield > 0) {
    drawShieldAura(ctx, player, distance);
  }

  ctx.save();
  ctx.globalAlpha = blink ? 0.52 : 1;
  ctx.translate(x, y + bob);

  ctx.fillStyle = 'rgba(28, 13, 8, 0.24)';
  ctx.beginPath();
  ctx.ellipse(width / 2, height + 7, width * 0.48, 6, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#f7edd0';
  roundedRect(ctx, 1, height - 14, width - 4, 16, 5);
  ctx.fill();
  ctx.strokeStyle = 'rgba(102, 69, 42, 0.4)';
  ctx.lineWidth = 1;
  ctx.stroke();

  const beefGradient = ctx.createLinearGradient(0, 10, width, 42);
  beefGradient.addColorStop(0, '#ad3c48');
  beefGradient.addColorStop(0.48, '#e59aa0');
  beefGradient.addColorStop(1, '#7e2530');

  for (let i = 0; i < 4; i += 1) {
    ctx.fillStyle = beefGradient;
    roundedRect(ctx, 13 + i * 4, 18 + i * 4, width - 22, 14, 8);
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 235, 222, 0.34)';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  const ryeTop = ctx.createLinearGradient(0, 2, 0, 29);
  ryeTop.addColorStop(0, '#f1d58f');
  ryeTop.addColorStop(1, '#9c6a32');
  ctx.fillStyle = ryeTop;
  roundedRect(ctx, 5, 5, width - 10, 24, 14);
  ctx.fill();

  const ryeBottom = ctx.createLinearGradient(0, 31, 0, 52);
  ryeBottom.addColorStop(0, '#d59a51');
  ryeBottom.addColorStop(1, '#7a4521');
  ctx.fillStyle = ryeBottom;
  roundedRect(ctx, 8, 34, width - 16, 16, 8);
  ctx.fill();

  ctx.fillStyle = '#f7e2aa';
  for (let i = 0; i < 9; i += 1) {
    ctx.beginPath();
    ctx.ellipse(17 + i * 7, 13 + (i % 2) * 4, 2.2, 1.2, 0.4, 0, Math.PI * 2);
    ctx.fill();
  }

  if (effects.double > 0) {
    ctx.fillStyle = 'rgba(195, 52, 66, 0.95)';
    roundedRect(ctx, width - 3, 6, 30, 21, 6);
    ctx.fill();
    ctx.fillStyle = '#fff1d7';
    ctx.font = '900 13px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('2X', width + 12, 17);
  }

  ctx.strokeStyle = '#6e4b08';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(width - 16, 0);
  ctx.lineTo(width - 22, 18);
  ctx.stroke();
  ctx.fillStyle = '#a8323e';
  ctx.beginPath();
  ctx.moveTo(width - 16, 0);
  ctx.lineTo(width - 3, 6);
  ctx.lineTo(width - 17, 11);
  ctx.closePath();
  ctx.fill();

  ctx.restore();
};

const drawPickleTub = (ctx, obstacle) => {
  const { x, y, width, height } = obstacle;
  ctx.fillStyle = '#f6e2ac';
  roundedRect(ctx, x + 4, y + 10, width - 8, height - 10, 7);
  ctx.fill();
  ctx.fillStyle = '#6f7a30';
  roundedRect(ctx, x + 8, y + 18, width - 16, height - 22, 6);
  ctx.fill();
  ctx.fillStyle = '#4c2818';
  ctx.fillRect(x + 9, y + 5, width - 18, 7);
  ctx.fillStyle = 'rgba(235, 242, 144, 0.55)';
  for (let i = 0; i < 4; i += 1) {
    ctx.beginPath();
    ctx.arc(x + 18 + i * 7, y + 28 + (i % 2) * 7, 2.2, 0, Math.PI * 2);
    ctx.fill();
  }
};

const drawMustardSpill = (ctx, obstacle) => {
  const { x, y, width, height } = obstacle;
  const puddle = ctx.createRadialGradient(x + width / 2, y + height / 2, 4, x + width / 2, y + height / 2, width / 2);
  puddle.addColorStop(0, '#f9db4b');
  puddle.addColorStop(1, '#bd8916');
  ctx.fillStyle = puddle;
  ctx.beginPath();
  ctx.ellipse(x + width / 2, y + height / 2 + 6, width * 0.48, height * 0.32, -0.12, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(255, 246, 190, 0.52)';
  ctx.beginPath();
  ctx.ellipse(x + width * 0.38, y + height * 0.48, width * 0.16, height * 0.08, -0.2, 0, Math.PI * 2);
  ctx.fill();
};

const drawTicketStack = (ctx, obstacle) => {
  const { x, y, width, height } = obstacle;
  for (let i = 0; i < 3; i += 1) {
    ctx.fillStyle = i === 0 ? '#fff1d2' : '#ead6ad';
    roundedRect(ctx, x + i * 3, y + i * 4, width - 8, height - 10, 4);
    ctx.fill();
    ctx.strokeStyle = 'rgba(166, 117, 46, 0.46)';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
  ctx.strokeStyle = 'rgba(88, 104, 57, 0.38)';
  ctx.lineWidth = 1;
  for (let i = 12; i < height - 12; i += 10) {
    ctx.beginPath();
    ctx.moveTo(x + 8, y + i);
    ctx.lineTo(x + width - 14, y + i);
    ctx.stroke();
  }
};

const drawObstacle = (ctx, obstacle) => {
  if (obstacle.type === 'pickle') {
    drawPickleTub(ctx, obstacle);
    return;
  }
  if (obstacle.type === 'mustard') {
    drawMustardSpill(ctx, obstacle);
    return;
  }
  drawTicketStack(ctx, obstacle);
};

const drawMeatSlice = (ctx, item) => {
  const { x, y, width, height, phase } = item;
  const bob = Math.sin(phase) * 2;
  const slice = ctx.createLinearGradient(x, y, x + width, y + height);
  slice.addColorStop(0, '#b5414b');
  slice.addColorStop(0.52, '#e49ba0');
  slice.addColorStop(1, '#7d252e');
  ctx.fillStyle = slice;
  roundedRect(ctx, x, y + bob, width, height, 12);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255, 236, 222, 0.44)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x + 7, y + height / 2 + bob);
  ctx.quadraticCurveTo(x + width / 2, y + 3 + bob, x + width - 7, y + height / 2 + bob);
  ctx.stroke();
};

const drawShieldPowerup = (ctx, item) => {
  const { x, y, width } = item;
  const cx = x + width / 2;
  const cy = y + width / 2;
  const coin = ctx.createRadialGradient(cx - 9, cy - 11, 4, cx, cy, 28);
  coin.addColorStop(0, '#fff3be');
  coin.addColorStop(0.42, '#d2a92f');
  coin.addColorStop(1, '#7a4b10');
  ctx.fillStyle = coin;
  ctx.beginPath();
  ctx.arc(cx, cy, width * 0.47, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#fff0aa';
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.strokeStyle = '#3b2815';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(cx, y + 10);
  ctx.quadraticCurveTo(x + 12, y + 15, x + 14, y + 27);
  ctx.quadraticCurveTo(cx, y + 39, x + width - 14, y + 27);
  ctx.quadraticCurveTo(x + width - 12, y + 15, cx, y + 10);
  ctx.stroke();
};

const drawDoublePowerup = (ctx, item) => {
  const { x, y, width } = item;
  for (let i = 0; i < 2; i += 1) {
    const offsetY = i * 8;
    const rye = ctx.createLinearGradient(x, y + offsetY, x, y + offsetY + 22);
    rye.addColorStop(0, '#f1d58f');
    rye.addColorStop(1, '#98642e');
    ctx.fillStyle = rye;
    roundedRect(ctx, x + 5, y + 8 + offsetY, width - 10, 17, 9);
    ctx.fill();
    ctx.fillStyle = '#b94755';
    roundedRect(ctx, x + 8, y + 20 + offsetY, width - 16, 8, 5);
    ctx.fill();
  }

  ctx.fillStyle = '#fff1d7';
  ctx.strokeStyle = '#a8323e';
  ctx.lineWidth = 4;
  ctx.font = '900 16px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.strokeText('2X', x + width / 2, y + 21);
  ctx.fillText('2X', x + width / 2, y + 21);
};

const drawMagnetPowerup = (ctx, item) => {
  const { x, y, width } = item;
  ctx.strokeStyle = '#a8323e';
  ctx.lineWidth = 8;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.arc(x + width / 2, y + width / 2 + 2, 14, Math.PI * 0.12, Math.PI * 0.88);
  ctx.stroke();
  ctx.strokeStyle = '#f6e2ac';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(x + 13, y + 27);
  ctx.lineTo(x + 10, y + 34);
  ctx.moveTo(x + width - 13, y + 27);
  ctx.lineTo(x + width - 10, y + 34);
  ctx.stroke();
};

const drawItem = (ctx, item, distance) => {
  const float = Math.sin(distance * 0.04 + item.phase) * 4;
  ctx.save();
  ctx.translate(0, float);
  ctx.fillStyle = item.kind === 'powerup' ? 'rgba(255, 237, 180, 0.22)' : 'rgba(255, 237, 180, 0.1)';
  ctx.beginPath();
  ctx.arc(item.x + item.width / 2, item.y + item.height / 2, item.kind === 'powerup' ? 28 : 20, 0, Math.PI * 2);
  ctx.fill();

  if (item.kind === 'slice') {
    drawMeatSlice(ctx, item);
  } else if (item.type === 'shield') {
    drawShieldPowerup(ctx, item);
  } else if (item.type === 'double') {
    drawDoublePowerup(ctx, item);
  } else {
    drawMagnetPowerup(ctx, item);
  }
  ctx.restore();
};

const getPlayerHitbox = (player) => ({
  x: player.x + 16,
  y: player.y + 12,
  width: player.width - 30,
  height: player.height - 22
});

const getObjectHitbox = (object) => ({
  x: object.x + 8,
  y: object.y + 8,
  width: object.width - 16,
  height: object.height - 16
});

const rectsOverlap = (a, b) => (
  a.x < b.x + b.width &&
  a.x + a.width > b.x &&
  a.y < b.y + b.height &&
  a.y + a.height > b.y
);

const collides = (player, object) => rectsOverlap(getPlayerHitbox(player), getObjectHitbox(object));

const setCombo = (game, text) => {
  game.comboText = { text, time: 1.1 };
};

const activatePowerup = (game, type) => {
  const detail = POWERUP_DETAILS[type];
  game.effects[type] = Math.max(game.effects[type], detail.duration);
  game.runScore += type === 'double' ? 35 : 25;
  setCombo(game, `${detail.label} +${type === 'double' ? 35 : 25}`);
};

const spawnSlice = (game, lane, xOffset = 0) => {
  const width = 38;
  const height = 24;
  game.items.push({
    kind: 'slice',
    x: game.width + 24 + xOffset,
    y: getLaneY(game, lane, height),
    width,
    height,
    lane,
    phase: Math.random() * Math.PI * 2
  });
};

const spawnPowerup = (game) => {
  const type = POWERUP_TYPES[Math.floor(Math.random() * POWERUP_TYPES.length)];
  const blockedLanes = game.obstacles
    .filter((obstacle) => obstacle.x > game.width - 80 && obstacle.x < game.width + 170)
    .map((obstacle) => obstacle.lane);
  const availableLanes = LANES.filter((lane) => !blockedLanes.includes(lane));
  const lanePool = availableLanes.length > 0 ? availableLanes : LANES;
  const lane = lanePool[Math.floor(Math.random() * lanePool.length)];
  const width = 44;
  const height = 44;

  game.items.push({
    kind: 'powerup',
    type,
    x: game.width + 34,
    y: getLaneY(game, lane, height),
    width,
    height,
    lane,
    phase: Math.random() * Math.PI * 2
  });
};

const spawnWave = (game) => {
  const waveLanes = shuffle(LANES);
  const canUseTwoObstacles = game.age > 18 && Math.random() < Math.min(0.38, (game.age - 18) / 70);
  const obstacleCount = canUseTwoObstacles ? 2 : 1;
  const obstacleLanes = waveLanes.slice(0, obstacleCount);
  const safeLanes = LANES.filter((lane) => !obstacleLanes.includes(lane));
  const obstacleTypes = ['pickle', 'mustard', 'tickets'];
  const sizeByType = {
    pickle: { width: 50, height: 48 },
    mustard: { width: 62, height: 38 },
    tickets: { width: 48, height: 52 }
  };

  obstacleLanes.forEach((lane, index) => {
    const type = obstacleTypes[Math.floor(Math.random() * obstacleTypes.length)];
    const size = sizeByType[type];
    game.obstacles.push({
      type,
      lane,
      x: game.width + 26 + index * 34,
      y: getLaneY(game, lane, size.height),
      width: size.width,
      height: size.height
    });
  });

  const guideLane = safeLanes[Math.floor(Math.random() * safeLanes.length)];
  spawnSlice(game, guideLane, 24);
  if (Math.random() < 0.62) {
    spawnSlice(game, guideLane, 92);
  }

  if (game.age < 24 && Math.random() < 0.72) {
    const extraLane = safeLanes.find((lane) => lane !== guideLane);
    if (extraLane !== undefined) spawnSlice(game, extraLane, 150);
  }
};

const collectItems = (game) => {
  const multiplier = game.effects.double > 0 ? 2 : 1;

  game.items = game.items.filter((item) => {
    if (!collides(game.player, item)) return true;

    if (item.kind === 'slice') {
      game.runScore += 12 * multiplier;
      return false;
    }

    activatePowerup(game, item.type);
    return false;
  });
};

const updateGame = (game, dt) => {
  const player = game.player;
  const targetY = getLaneY(game, player.lane, player.height);
  const scoreMultiplier = game.effects.double > 0 ? 2 : 1;

  Object.keys(game.effects).forEach((key) => {
    game.effects[key] = Math.max(0, game.effects[key] - dt);
  });

  game.age += dt;
  game.distance += game.speed * dt;
  game.speed = Math.min(340, 178 + Math.max(0, game.age - 3) * 2.8);
  game.runScore += (game.speed * dt * scoreMultiplier) / 11;
  game.spawnTimer -= dt;
  game.powerupTimer -= dt;
  game.shake = Math.max(0, game.shake - 24 * dt);
  game.hitFlash = Math.max(0, game.hitFlash - dt);
  game.invincible = Math.max(0, game.invincible - dt);
  game.comboText.time = Math.max(0, game.comboText.time - dt);
  player.y += (targetY - player.y) * Math.min(1, dt * 13);

  if (game.spawnTimer <= 0) {
    if (game.age < 4.5) {
      spawnSlice(game, 1, 20);
      spawnSlice(game, 1, 88);
    } else {
      spawnWave(game);
    }
    game.spawnTimer = Math.max(1.05, 1.82 - game.age * 0.009) + Math.random() * 0.55;
  }

  if (game.powerupTimer <= 0) {
    spawnPowerup(game);
    game.powerupTimer = 6.1 + Math.random() * 2.1;
  }

  game.obstacles.forEach((obstacle) => {
    obstacle.x -= game.speed * dt;
  });

  game.items.forEach((item) => {
    item.phase += dt * 5;
    item.x -= game.speed * dt;

    if (game.effects.magnet > 0 && item.kind === 'slice' && item.x - player.x < 250) {
      item.x -= game.speed * dt * 0.42;
      item.y += (player.y + player.height / 2 - item.y - item.height / 2) * Math.min(1, dt * 5);
    }
  });

  game.obstacles = game.obstacles.filter((obstacle) => obstacle.x + obstacle.width > -32);
  game.items = game.items.filter((item) => item.x + item.width > -32);
  collectItems(game);
};

const drawComboText = (ctx, game) => {
  if (game.comboText.time <= 0) return;

  const alpha = Math.min(1, game.comboText.time);
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.font = '900 20px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = 5;
  ctx.strokeStyle = 'rgba(48, 24, 12, 0.62)';
  ctx.fillStyle = '#fff0b6';
  ctx.strokeText(game.comboText.text, game.width / 2, 122);
  ctx.fillText(game.comboText.text, game.width / 2, 122);
  ctx.restore();
};

const drawLives = (ctx, game) => {
  ctx.save();
  ctx.translate(18, 112);
  for (let i = 0; i < 3; i += 1) {
    ctx.fillStyle = i < game.lives ? '#c33242' : 'rgba(255,255,255,0.2)';
    roundedRect(ctx, i * 28, 0, 20, 14, 4);
    ctx.fill();
    ctx.fillStyle = i < game.lives ? '#f7e2aa' : 'rgba(255,255,255,0.22)';
    ctx.fillRect(i * 28 + 3, 3, 14, 2);
  }
  ctx.restore();
};

const drawGame = (ctx, game, status) => {
  drawCounter(ctx, game);

  ctx.save();
  if (game.shake > 0) {
    ctx.translate((Math.random() - 0.5) * game.shake, (Math.random() - 0.5) * game.shake * 0.55);
  }

  game.items.forEach((item) => drawItem(ctx, item, game.distance));
  game.obstacles.forEach((obstacle) => drawObstacle(ctx, obstacle));
  drawCornedBeefRunner(ctx, game.player, game.distance, game.effects, game.invincible);
  ctx.restore();

  drawLives(ctx, game);
  drawComboText(ctx, game);

  if (game.hitFlash > 0) {
    ctx.fillStyle = `rgba(168, 50, 62, ${game.hitFlash * 0.5})`;
    ctx.fillRect(0, 0, game.width, game.height);
  }

  if (status !== 'running') {
    ctx.fillStyle = 'rgba(42, 24, 17, 0.38)';
    ctx.fillRect(0, 0, game.width, game.height);
  }
};

const STACKER_COLUMNS = 10;
const STACKER_ROWS = 20;

const STACKER_PIECES = [
  { key: 'I', type: 'pastrami', name: 'Pastrami', shape: [[1, 1, 1, 1]] },
  { key: 'O', type: 'rye', name: 'Rye', shape: [[1, 1], [1, 1]] },
  { key: 'T', type: 'pickle', name: 'Pickle', shape: [[0, 1, 0], [1, 1, 1]] },
  { key: 'J', type: 'swiss', name: 'Swiss', shape: [[1, 0, 0], [1, 1, 1]] },
  { key: 'L', type: 'mustard', name: 'Mustard', shape: [[0, 0, 1], [1, 1, 1]] },
  { key: 'S', type: 'slaw', name: 'Slaw', shape: [[0, 1, 1], [1, 1, 0]] },
  { key: 'Z', type: 'beef', name: 'Beef', shape: [[1, 1, 0], [0, 1, 1]] }
];

const STACKER_KICK_TESTS = {
  normal: {
    '0>1': [[0, 0], [-1, 0], [-1, 1], [0, -2], [-1, -2]],
    '1>0': [[0, 0], [1, 0], [1, -1], [0, 2], [1, 2]],
    '1>2': [[0, 0], [1, 0], [1, -1], [0, 2], [1, 2]],
    '2>1': [[0, 0], [-1, 0], [-1, 1], [0, -2], [-1, -2]],
    '2>3': [[0, 0], [1, 0], [1, 1], [0, -2], [1, -2]],
    '3>2': [[0, 0], [-1, 0], [-1, -1], [0, 2], [-1, 2]],
    '3>0': [[0, 0], [-1, 0], [-1, -1], [0, 2], [-1, 2]],
    '0>3': [[0, 0], [1, 0], [1, 1], [0, -2], [1, -2]]
  },
  I: {
    '0>1': [[0, 0], [-2, 0], [1, 0], [-2, -1], [1, 2]],
    '1>0': [[0, 0], [2, 0], [-1, 0], [2, 1], [-1, -2]],
    '1>2': [[0, 0], [-1, 0], [2, 0], [-1, 2], [2, -1]],
    '2>1': [[0, 0], [1, 0], [-2, 0], [1, -2], [-2, 1]],
    '2>3': [[0, 0], [2, 0], [-1, 0], [2, 1], [-1, -2]],
    '3>2': [[0, 0], [-2, 0], [1, 0], [-2, -1], [1, 2]],
    '3>0': [[0, 0], [1, 0], [-2, 0], [1, -2], [-2, 1]],
    '0>3': [[0, 0], [-1, 0], [2, 0], [-1, 2], [2, -1]]
  }
};

const MINI_GAME_ACTIONABLES = [
  {
    title: 'Deli Stacker',
    detail: 'Falling deli blocks',
    status: 'Playable'
  },
  {
    title: 'Counter Cruise',
    detail: 'Three-lane counter dash',
    status: 'Playable'
  },
  {
    title: 'Pickle Pop',
    detail: 'Quick reaction board',
    status: 'Playable'
  }
];

const makeEmptyStackerBoard = () => Array.from({ length: STACKER_ROWS }, () => Array(STACKER_COLUMNS).fill(null));

const copyShape = (shape) => shape.map((row) => [...row]);

const makeStackerPiece = (piece) => ({
  key: piece.key,
  type: piece.type,
  name: piece.name,
  shape: copyShape(piece.shape),
  baseShape: copyShape(piece.shape),
  rotation: 0
});

const makeStackerBag = () => shuffle(STACKER_PIECES).map(makeStackerPiece);

const refillStackerQueue = (queue) => {
  const nextQueue = [...queue];
  while (nextQueue.length < 7) {
    nextQueue.push(...makeStackerBag());
  }
  return nextQueue;
};

const drawStackerPieceFromQueue = (queue) => {
  const refilledQueue = refillStackerQueue(queue);
  const [piece, ...remainingQueue] = refilledQueue;
  return {
    piece,
    queue: remainingQueue
  };
};

const resetStackerPiece = (piece) => ({
  ...piece,
  shape: copyShape(piece.baseShape),
  rotation: 0,
  x: undefined,
  y: undefined
});

const placeStackerPiece = (piece) => ({
  ...resetStackerPiece(piece),
  x: Math.floor(STACKER_COLUMNS / 2) - Math.ceil(piece.shape[0].length / 2),
  y: -1
});

const rotateStackerShapeClockwise = (shape) => shape[0].map((_, columnIndex) => (
  shape.map((row) => row[columnIndex]).reverse()
));

const rotateStackerShapeCounterclockwise = (shape) => shape[0].map((_, columnIndex) => (
  shape.map((row) => row[shape[0].length - 1 - columnIndex])
));

const isStackerPositionValid = (board, piece, nextX = piece.x, nextY = piece.y, nextShape = piece.shape) => {
  for (let row = 0; row < nextShape.length; row += 1) {
    for (let column = 0; column < nextShape[row].length; column += 1) {
      if (!nextShape[row][column]) continue;

      const boardX = nextX + column;
      const boardY = nextY + row;

      if (boardX < 0 || boardX >= STACKER_COLUMNS || boardY >= STACKER_ROWS) {
        return false;
      }

      if (boardY >= 0 && board[boardY][boardX]) {
        return false;
      }
    }
  }

  return true;
};

const mergeStackerPiece = (board, piece) => {
  const nextBoard = board.map((row) => [...row]);

  piece.shape.forEach((row, rowIndex) => {
    row.forEach((cell, columnIndex) => {
      if (!cell) return;
      const boardY = piece.y + rowIndex;
      const boardX = piece.x + columnIndex;
      if (boardY >= 0 && boardY < STACKER_ROWS && boardX >= 0 && boardX < STACKER_COLUMNS) {
        nextBoard[boardY][boardX] = piece.type;
      }
    });
  });

  return nextBoard;
};

const clearStackerRows = (board) => {
  const remainingRows = board.filter((row) => !row.every(Boolean));
  const cleared = board.length - remainingRows.length;
  const emptyRows = Array.from({ length: cleared }, () => Array(STACKER_COLUMNS).fill(null));
  return {
    board: [...emptyRows, ...remainingRows],
    cleared
  };
};

const getStackerDropDistance = (board, piece) => {
  let dropDistance = 0;
  while (isStackerPositionValid(board, piece, piece.x, piece.y + dropDistance + 1)) {
    dropDistance += 1;
  }
  return dropDistance;
};

const getGhostStackerPiece = (state) => ({
  ...state.piece,
  y: state.piece.y + getStackerDropDistance(state.board, state.piece)
});

const getStackerViewBoard = (state) => {
  const viewBoard = state.board.map((row) => [...row]);
  const piece = state.piece;
  const ghostPiece = getGhostStackerPiece(state);

  ghostPiece.shape.forEach((row, rowIndex) => {
    row.forEach((cell, columnIndex) => {
      if (!cell) return;
      const boardY = ghostPiece.y + rowIndex;
      const boardX = ghostPiece.x + columnIndex;
      if (boardY >= 0 && boardY < STACKER_ROWS && boardX >= 0 && boardX < STACKER_COLUMNS && !viewBoard[boardY][boardX]) {
        viewBoard[boardY][boardX] = `ghost-${ghostPiece.type}`;
      }
    });
  });

  piece.shape.forEach((row, rowIndex) => {
    row.forEach((cell, columnIndex) => {
      if (!cell) return;
      const boardY = piece.y + rowIndex;
      const boardX = piece.x + columnIndex;
      if (boardY >= 0 && boardY < STACKER_ROWS && boardX >= 0 && boardX < STACKER_COLUMNS) {
        viewBoard[boardY][boardX] = piece.type;
      }
    });
  });

  return viewBoard;
};

const createStackerState = () => {
  const firstDraw = drawStackerPieceFromQueue([]);
  const secondDraw = drawStackerPieceFromQueue(firstDraw.queue);

  return {
    board: makeEmptyStackerBoard(),
    piece: placeStackerPiece(firstDraw.piece),
    nextPiece: secondDraw.piece,
    heldPiece: null,
    canHold: true,
    queue: secondDraw.queue,
    score: 0,
    lines: 0,
    level: 1,
    status: 'ready'
  };
};

const lockStackerPiece = (state, landedPiece = state.piece) => {
  const mergedBoard = mergeStackerPiece(state.board, landedPiece);
  const { board, cleared } = clearStackerRows(mergedBoard);
  const nextLines = state.lines + cleared;
  const nextLevel = Math.floor(nextLines / 6) + 1;
  const nextPiece = placeStackerPiece(state.nextPiece);
  const nextDraw = drawStackerPieceFromQueue(state.queue);
  const upcomingPiece = nextDraw.piece;
  const lineScores = [0, 100, 300, 500, 800];
  const nextScore = state.score + 8 + (lineScores[cleared] || 0) * nextLevel;

  if (!isStackerPositionValid(board, nextPiece)) {
    return {
      ...state,
      board,
      piece: nextPiece,
      nextPiece: upcomingPiece,
      queue: nextDraw.queue,
      canHold: true,
      score: nextScore,
      lines: nextLines,
      level: nextLevel,
      status: 'ended'
    };
  }

  return {
    ...state,
    board,
    piece: nextPiece,
    nextPiece: upcomingPiece,
    queue: nextDraw.queue,
    canHold: true,
    score: nextScore,
    lines: nextLines,
    level: nextLevel
  };
};

const tryRotateStackerPiece = (state, direction) => {
  if (state.piece.key === 'O') return state;

  const fromRotation = state.piece.rotation;
  const toRotation = (fromRotation + direction + 4) % 4;
  const nextShape = direction === 1
    ? rotateStackerShapeClockwise(state.piece.shape)
    : rotateStackerShapeCounterclockwise(state.piece.shape);
  const kickType = state.piece.key === 'I' ? 'I' : 'normal';
  const kickTests = STACKER_KICK_TESTS[kickType][`${fromRotation}>${toRotation}`] || [[0, 0]];
  const validKick = kickTests.find(([kickX, kickY]) => isStackerPositionValid(
    state.board,
    state.piece,
    state.piece.x + kickX,
    state.piece.y - kickY,
    nextShape
  ));

  if (!validKick) return state;

  const [kickX, kickY] = validKick;
  return {
    ...state,
    piece: {
      ...state.piece,
      x: state.piece.x + kickX,
      y: state.piece.y - kickY,
      shape: nextShape,
      rotation: toRotation
    }
  };
};

const holdStackerPiece = (state) => {
  if (!state.canHold) return state;

  const heldPiece = resetStackerPiece(state.piece);

  if (state.heldPiece) {
    const nextPiece = placeStackerPiece(state.heldPiece);
    if (!isStackerPositionValid(state.board, nextPiece)) {
      return {
        ...state,
        heldPiece,
        piece: nextPiece,
        canHold: false,
        status: 'ended'
      };
    }

    return {
      ...state,
      heldPiece,
      piece: nextPiece,
      canHold: false
    };
  }

  const nextPiece = placeStackerPiece(state.nextPiece);
  const nextDraw = drawStackerPieceFromQueue(state.queue);

  if (!isStackerPositionValid(state.board, nextPiece)) {
    return {
      ...state,
      heldPiece,
      piece: nextPiece,
      nextPiece: nextDraw.piece,
      queue: nextDraw.queue,
      canHold: false,
      status: 'ended'
    };
  }

  return {
    ...state,
    heldPiece,
    piece: nextPiece,
    nextPiece: nextDraw.piece,
    queue: nextDraw.queue,
    canHold: false
  };
};

const advanceStackerPiece = (state) => {
  if (isStackerPositionValid(state.board, state.piece, state.piece.x, state.piece.y + 1)) {
    return {
      ...state,
      piece: {
        ...state.piece,
        y: state.piece.y + 1
      }
    };
  }

  return lockStackerPiece(state);
};

const stackerReducer = (state, action) => {
  if (action.type === 'START') {
    return {
      ...createStackerState(),
      status: 'running'
    };
  }

  if (action.type === 'PAUSE') {
    if (state.status === 'running') return { ...state, status: 'paused' };
    if (state.status === 'paused') return { ...state, status: 'running' };
    return state;
  }

  if (state.status !== 'running') {
    return state;
  }

  if (action.type === 'TICK') {
    return advanceStackerPiece(state);
  }

  if (action.type === 'MOVE') {
    const nextX = state.piece.x + action.direction;
    if (!isStackerPositionValid(state.board, state.piece, nextX)) return state;
    return {
      ...state,
      piece: {
        ...state.piece,
        x: nextX
      }
    };
  }

  if (action.type === 'SOFT_DROP') {
    if (!isStackerPositionValid(state.board, state.piece, state.piece.x, state.piece.y + 1)) {
      return lockStackerPiece(state);
    }
    return {
      ...state,
      score: state.score + 1,
      piece: {
        ...state.piece,
        y: state.piece.y + 1
      }
    };
  }

  if (action.type === 'ROTATE') {
    return tryRotateStackerPiece(state, action.direction || 1);
  }

  if (action.type === 'HARD_DROP') {
    const dropDistance = getStackerDropDistance(state.board, state.piece);

    const landedPiece = {
      ...state.piece,
      y: state.piece.y + dropDistance
    };

    return lockStackerPiece(
      {
        ...state,
        score: state.score + dropDistance * 2
      },
      landedPiece
    );
  }

  if (action.type === 'HOLD') {
    return holdStackerPiece(state);
  }

  return state;
};

const StackerPiecePreview = ({ piece }) => (
  <div className="stacker-preview" style={{ '--preview-columns': piece ? piece.shape[0].length : 2 }}>
    {piece ? piece.shape.flatMap((row, rowIndex) => row.map((cell, columnIndex) => (
      <span
        className={`stacker-cell ${cell ? `stacker-cell-${piece.type}` : 'stacker-cell-ghost'}`}
        key={`${rowIndex}-${columnIndex}`}
      />
    ))) : Array.from({ length: 4 }, (_, index) => (
      <span className="stacker-cell stacker-cell-ghost" key={index} />
    ))}
  </div>
);

const DeliStackerGame = () => {
  const [state, dispatch] = React.useReducer(stackerReducer, undefined, createStackerState);
  const stackerRef = useRef(null);
  const board = getStackerViewBoard(state);
  const intervalDelay = Math.max(170, 760 - state.level * 62);

  const focusStacker = useCallback(() => {
    window.requestAnimationFrame(() => {
      stackerRef.current?.focus({ preventScroll: true });
    });
  }, []);

  const startStacker = useCallback(() => {
    dispatch({ type: 'START' });
    focusStacker();
  }, [focusStacker]);

  useEffect(() => {
    if (state.status !== 'running') return undefined;
    const timer = window.setInterval(() => dispatch({ type: 'TICK' }), intervalDelay);
    return () => window.clearInterval(timer);
  }, [intervalDelay, state.status]);

  const handleKeyDown = (event) => {
    const keyMap = {
      ArrowLeft: { type: 'MOVE', direction: -1 },
      ArrowRight: { type: 'MOVE', direction: 1 },
      ArrowDown: { type: 'SOFT_DROP' },
      ArrowUp: { type: 'ROTATE', direction: 1 },
      z: { type: 'ROTATE', direction: -1 },
      Z: { type: 'ROTATE', direction: -1 },
      x: { type: 'ROTATE', direction: 1 },
      X: { type: 'ROTATE', direction: 1 },
      c: { type: 'HOLD' },
      C: { type: 'HOLD' },
      ' ': { type: 'HARD_DROP' }
    };
    const action = keyMap[event.key];
    if (!action) return;
    event.preventDefault();
    event.stopPropagation();
    dispatch(action);
  };

  const handleBoardPointerDown = (event) => {
    event.preventDefault();
    focusStacker();

    if (state.status !== 'running') {
      dispatch({ type: 'START' });
      return;
    }

    dispatch({ type: 'ROTATE', direction: 1 });
  };

  return (
    <section className="mini-game-panel stacker-panel" tabIndex="0" onKeyDown={handleKeyDown} ref={stackerRef}>
      <div className="container">
        <div className="stacker-shell">
          <div className="stacker-board-wrap">
            <div className="stacker-board" aria-label="Deli Stacker board" role="img" onPointerDown={handleBoardPointerDown}>
              {board.map((row, rowIndex) => row.map((cell, columnIndex) => (
                <span
                  className={`stacker-cell ${cell ? `stacker-cell-${cell}` : ''}`}
                  key={`${rowIndex}-${columnIndex}`}
                />
              )))}
            </div>
          </div>

          <div className="stacker-side">
            <span className="game-kicker">Featured</span>
            <h2 className="force-revue">Deli Stacker</h2>
            <p>Falling rye, pastrami, pickle, Swiss, mustard, slaw, and beef blocks.</p>

            <div className="stacker-stats">
              <span>
                <strong>{state.score}</strong>
                Score
              </span>
              <span>
                <strong>{state.lines}</strong>
                Lines
              </span>
              <span>
                <strong>{state.level}</strong>
                Level
              </span>
            </div>

            <div className="stacker-next">
              <span>Next Ticket</span>
              <StackerPiecePreview piece={state.nextPiece} />
            </div>

            <div className="stacker-next">
              <span>Held Ticket</span>
              <StackerPiecePreview piece={state.heldPiece} />
            </div>

            <div className="stacker-controls">
              <button className="btn primary" type="button" onClick={startStacker}>
                {state.status === 'ended' ? 'Restack' : 'Start'}
              </button>
              <button className="btn secondary" type="button" onClick={() => dispatch({ type: 'HOLD' })} disabled={state.status !== 'running' || !state.canHold}>
                Hold
              </button>
              <button className="btn secondary" type="button" onClick={() => dispatch({ type: 'PAUSE' })}>
                {state.status === 'paused' ? 'Resume' : 'Pause'}
              </button>
            </div>

            <div className="stacker-controls stacker-move-controls" aria-label="Deli Stacker controls">
              <button type="button" onClick={() => dispatch({ type: 'MOVE', direction: -1 })}>Left</button>
              <button type="button" onClick={() => dispatch({ type: 'ROTATE', direction: -1 })}>Turn L</button>
              <button type="button" onClick={() => dispatch({ type: 'ROTATE', direction: 1 })}>Turn R</button>
              <button type="button" onClick={() => dispatch({ type: 'MOVE', direction: 1 })}>Right</button>
              <button type="button" onClick={() => dispatch({ type: 'HARD_DROP' })}>Drop</button>
            </div>

            {state.status !== 'running' && (
              <div className="stacker-status">
                {state.status === 'ended' ? 'Board closed' : state.status === 'paused' ? 'Ticket held' : 'Ready'}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const PicklePopGame = () => {
  const [status, setStatus] = useState('ready');
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(25);
  const [activeJar, setActiveJar] = useState(4);

  const startGame = useCallback(() => {
    setStatus('running');
    setScore(0);
    setTimeLeft(25);
    setActiveJar(Math.floor(Math.random() * 9));
  }, []);

  useEffect(() => {
    if (status !== 'running') return undefined;

    const jarTimer = window.setInterval(() => {
      setActiveJar((current) => {
        let next = Math.floor(Math.random() * 9);
        if (next === current) next = (next + 3) % 9;
        return next;
      });
    }, 780);

    const clockTimer = window.setInterval(() => {
      setTimeLeft((current) => {
        if (current <= 1) {
          setStatus('ended');
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(jarTimer);
      window.clearInterval(clockTimer);
    };
  }, [status]);

  const popJar = (index) => {
    if (status !== 'running') {
      startGame();
      return;
    }

    if (index !== activeJar) return;
    setScore((current) => current + 10);
    setActiveJar((current) => (current + 4 + Math.floor(Math.random() * 4)) % 9);
  };

  return (
    <section className="mini-game-panel pickle-panel">
      <div className="container">
        <div className="pickle-shell">
          <div className="pickle-copy">
            <span className="game-kicker">Quick Play</span>
            <h2 className="force-revue">Pickle Pop</h2>
            <p>A fast little jar counter for short visits.</p>
            <div className="stacker-stats">
              <span>
                <strong>{score}</strong>
                Score
              </span>
              <span>
                <strong>{timeLeft}</strong>
                Time
              </span>
            </div>
            <button className="btn primary" type="button" onClick={startGame}>
              {status === 'ended' ? 'Pop Again' : 'Start'}
            </button>
          </div>

          <div className="pickle-grid" aria-label="Pickle Pop board">
            {Array.from({ length: 9 }, (_, index) => (
              <button
                className={`pickle-jar ${status === 'running' && index === activeJar ? 'pickle-jar-active' : ''}`}
                key={index}
                type="button"
                onClick={() => popJar(index)}
              >
                <span />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const MiniGamesPage = () => {
  const canvasRef = useRef(null);
  const frameRef = useRef(null);
  const statusRef = useRef('ready');
  const scoreRef = useRef(0);
  const bestRef = useRef(0);
  const gameRef = useRef(makeGameState());
  const effectsKeyRef = useRef('0-0-0');
  const [status, setStatus] = useState('ready');
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(0);
  const [lives, setLives] = useState(3);
  const [activeEffects, setActiveEffects] = useState({ shield: 0, double: 0, magnet: 0 });

  const publishEffects = useCallback((game) => {
    const snapshot = {
      shield: Math.ceil(game.effects.shield),
      double: Math.ceil(game.effects.double),
      magnet: Math.ceil(game.effects.magnet)
    };
    const key = `${snapshot.shield}-${snapshot.double}-${snapshot.magnet}`;
    if (key !== effectsKeyRef.current) {
      effectsKeyRef.current = key;
      setActiveEffects(snapshot);
    }
  }, []);

  useEffect(() => {
    const savedBest = Number(window.localStorage.getItem(BEST_SCORE_KEY) || 0);
    bestRef.current = savedBest;
    setBest(savedBest);
  }, []);

  const syncCanvasSize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const width = Math.max(320, Math.round(rect.width));
    const height = Math.max(300, Math.round(rect.height));
    const ratio = window.devicePixelRatio || 1;

    if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(height * ratio)) {
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      const ctx = canvas.getContext('2d');
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    }

    const game = gameRef.current;
    game.width = width;
    game.height = height;
    game.groundY = height - 62;
    game.player.y = getLaneY(game, game.player.lane, game.player.height);
    game.obstacles.forEach((obstacle) => {
      obstacle.y = getLaneY(game, obstacle.lane, obstacle.height);
    });
    game.items.forEach((item) => {
      item.y = getLaneY(game, item.lane, item.height);
    });
  }, []);

  const startRun = useCallback(() => {
    const canvas = canvasRef.current;
    const rect = canvas?.getBoundingClientRect();
    const width = rect ? Math.max(320, Math.round(rect.width)) : 900;
    const height = rect ? Math.max(300, Math.round(rect.height)) : 360;
    gameRef.current = makeGameState(width, height);
    statusRef.current = 'running';
    scoreRef.current = 0;
    effectsKeyRef.current = '0-0-0';
    setActiveEffects({ shield: 0, double: 0, magnet: 0 });
    setLives(3);
    setScore(0);
    setStatus('running');
  }, []);

  const moveLane = useCallback((direction) => {
    if (statusRef.current !== 'running') {
      startRun();
      return;
    }

    const player = gameRef.current.player;
    player.lane = Math.max(0, Math.min(2, player.lane + direction));
  }, [startRun]);

  const setLaneFromCanvas = useCallback((event) => {
    if (statusRef.current !== 'running') {
      startRun();
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const y = event.clientY - rect.top;
    const game = gameRef.current;
    const laneCenters = getLaneCenters(game);
    const closestLane = laneCenters.reduce((bestLane, center, lane) => {
      const bestDistance = Math.abs(laneCenters[bestLane] - y);
      return Math.abs(center - y) < bestDistance ? lane : bestLane;
    }, 0);
    game.player.lane = closestLane;
  }, [startRun]);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const handleResize = () => {
      syncCanvasSize();
      drawGame(ctx, gameRef.current, statusRef.current);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    const finishRun = () => {
      statusRef.current = 'ended';
      setStatus('ended');
      const finalScore = scoreRef.current;
      if (finalScore > bestRef.current) {
        bestRef.current = finalScore;
        setBest(finalScore);
        window.localStorage.setItem(BEST_SCORE_KEY, String(finalScore));
      }
    };

    const loop = (time) => {
      const game = gameRef.current;
      const dt = game.lastTime ? Math.min((time - game.lastTime) / 1000, 0.034) : 0;
      game.lastTime = time;

      if (statusRef.current === 'running') {
        updateGame(game, dt);
        publishEffects(game);

        const nextScore = Math.floor(game.runScore);
        if (nextScore !== scoreRef.current) {
          scoreRef.current = nextScore;
          setScore(nextScore);
        }

        const hitIndex = game.obstacles.findIndex((obstacle) => collides(game.player, obstacle));
        if (hitIndex >= 0 && game.invincible <= 0) {
          if (game.effects.shield > 0) {
            game.effects.shield = 0;
            game.obstacles.splice(hitIndex, 1);
            game.shake = 9;
            game.hitFlash = 0.12;
            game.runScore += 35;
            setCombo(game, 'Shield Save +35');
            publishEffects(game);
          } else {
            game.lives -= 1;
            game.obstacles.splice(hitIndex, 1);
            game.invincible = 1.45;
            game.shake = 12;
            game.hitFlash = 0.22;
            setLives(game.lives);
            if (game.lives <= 0) {
              finishRun();
            } else {
              setCombo(game, `${game.lives} tickets left`);
            }
          }
        }
      }

      drawGame(ctx, game, statusRef.current);
      frameRef.current = window.requestAnimationFrame(loop);
    };

    frameRef.current = window.requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.cancelAnimationFrame(frameRef.current);
    };
  }, [publishEffects, syncCanvasSize]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.target instanceof Element && event.target.closest('.stacker-panel, .pickle-panel')) return;
      if (![' ', 'ArrowUp', 'ArrowDown', 'w', 'W', 's', 'S'].includes(event.key)) return;
      event.preventDefault();

      if (event.key === 'ArrowUp' || event.key === 'w' || event.key === 'W') {
        moveLane(-1);
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 's' || event.key === 'S') {
        moveLane(1);
        return;
      }

      if (statusRef.current !== 'running') {
        startRun();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [moveLane, startRun]);

  return (
    <div className="mini-games-page">
      <section className="mini-games-hero">
        <div className="container">
          <h1 className="force-revue">Starcade</h1>
          <p className="subtitle">A tiny arcade tucked behind the deli counter.</p>
        </div>
      </section>

      <section className="mini-game-actionables">
        <div className="container">
          <div className="actionable-grid">
            {MINI_GAME_ACTIONABLES.map((game) => (
              <article className="actionable-card" key={game.title}>
                <span>{game.status}</span>
                <h2 className="force-revue">{game.title}</h2>
                <p>{game.detail}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <DeliStackerGame />

      <section className="mini-games-arcade">
        <div className="container">
          <div className="game-cabinet">
            <div className="game-marquee">
              <div className="game-title">
                <h2 className="force-revue">Corned Beef Counter Cruise</h2>
                <p>Counter-side deli dash</p>
              </div>

              <div className="game-powerups" aria-live="polite">
                {Object.entries(POWERUP_DETAILS).map(([key, detail]) => (
                  <span
                    className={`powerup-chip ${activeEffects[key] > 0 ? 'powerup-chip-active' : ''}`}
                    key={key}
                  >
                    <strong>{detail.label}</strong>
                    {activeEffects[key] > 0 ? `${activeEffects[key]}s` : 'Ready'}
                  </span>
                ))}
              </div>

              <div className="game-scoreboard" aria-live="polite">
                <span>
                  <strong>{score}</strong>
                  Score
                </span>
                <span>
                  <strong>{best}</strong>
                  Best
                </span>
                <span>
                  <strong>{lives}</strong>
                  Tickets
                </span>
              </div>
            </div>

            <div className={`runner-stage runner-stage-${status}`}>
              <canvas
                ref={canvasRef}
                className="runner-canvas"
                width="900"
                height="360"
                onPointerDown={setLaneFromCanvas}
                aria-label="Corned Beef Counter Cruise game canvas"
                role="img"
              />

              {status !== 'running' && (
                <div className="runner-overlay">
                  <div className="runner-ticket">
                    <span className="ticket-kicker">Star Deli Starcade</span>
                    <h3>{status === 'ended' ? 'Counter Closed' : 'Counter Cruise'}</h3>
                    {status === 'ended' && <p>Score {score}</p>}
                    <button className="btn primary" type="button" onClick={startRun}>
                      {status === 'ended' ? 'Run Again' : 'Start Run'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="game-controls">
              <div className="lane-controls">
                <button className="lane-button" type="button" onClick={() => moveLane(-1)} aria-label="Move up">
                  Up
                </button>
                <button className="lane-button" type="button" onClick={() => moveLane(1)} aria-label="Move down">
                  Down
                </button>
              </div>
              <div className="game-tokens" aria-hidden="true">
                <span>Deli Shield</span>
                <span>Double Deck</span>
                <span>Counter Magnet</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PicklePopGame />
    </div>
  );
};

export default MiniGamesPage;
